import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import ReactModal from 'react-modal';
import { IconButton } from 'components/IconButton';
import { Heading, Paragraph, ParagraphProps } from 'components/Typography';
import { FlashMessageScope } from 'domains/flash-messages/FlashMessageScope';

export type Props = {
  open: boolean;
  onRequestClose: () => void;
  dismissable?: boolean;
  children?: React.ReactNode | ((args: { close: () => void }) => React.ReactNode);
  flashScope?: string;
};

export const Modal = ({ open, onRequestClose, dismissable = true, children, flashScope }: Props): JSX.Element => {
  const { t } = useTranslation();
  const childComponents = typeof children === 'function' ? children({ close: onRequestClose }) : children;

  return (
    <ReactModal
      isOpen={open}
      appElement={document.querySelector('#app') as HTMLElement}
      closeTimeoutMS={200}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc={dismissable}
      shouldCloseOnOverlayClick={dismissable}
      className={{
        base: 'inline-block w-[80%] max-w-md md:max-w-xl lg:max-w-2xl p-24 md:p-32 my-24 md:my-32 overflow-hidden text-left align-middle bg-white shadow-xl rounded-md focus-visible:outline-none transform opacity-0 scale-95 transition-all origin-center',
        afterOpen:
          'opacity-100 scale-100 ease-out duration-300 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ',
        beforeClose: '!opacity-0 !scale-95 !ease-in !duration-200',
      }}
      overlayClassName={{
        base: 'fixed inset-0 z-50 bg-black-60% overflow-y-auto text-center opacity-0 transition-opacity px-20',
        afterOpen: 'opacity-100 ease-out duration-300',
        beforeClose: '!opacity-0 !ease-in !duration-200',
      }}
    >
      {dismissable && (
        <IconButton
          title={t('modal.dismissButtonText')}
          name="Close"
          variant="ghost"
          size="sm"
          onClick={onRequestClose}
          className="float-right -mt-16 -mr-16 md:-mt-24 md:-mr-24 ml-8"
        />
      )}

      {flashScope ? <FlashMessageScope scope={flashScope}>{childComponents}</FlashMessageScope> : childComponents}
    </ReactModal>
  );
};

Modal.DefaultTitle = function ModalTitle({ children, className }: { children: string; className?: string }) {
  return (
    <Heading level="h2" as="h1" className={cx(className, 'mb-16 md:mb-20')}>
      {children}
    </Heading>
  );
};

Modal.DefaultDescription = function ModalDescription(props: ParagraphProps<'p'>) {
  return (
     
    <Paragraph {...props} />
  );
};
